import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, CalendarRange, Check, Play, Trash2 } from "lucide-react";
import {
  MIX_BUCKETS,
  MIX_PRESETS,
  daysInMonth,
  type ContentMix,
  type MixBucket,
} from "@marketing/shared";
import { apiErrorMessage } from "@/lib/api";
import { useBrands } from "@/lib/queries";
import {
  useCancelMonthlyPlan,
  useCreateMonthlyPlan,
  useGenerateMonthlyPlan,
  useMonthlyPlans,
  usePreviewMonth,
  type MonthPreview,
} from "@/lib/campaigns";
import { BatchProgress } from "@/components/BatchProgress";
import { PagePicker } from "@/components/PagePicker";
import { ErrorBox } from "./LoginPage";

/**
 * "Plan my month" (spec §6 flow 5, §8).
 *
 * Three steps, deliberately: how many posts, what mix, then a CALENDAR PREVIEW
 * before confirming. The preview is the step that makes the feature trustworthy
 * — "about 20 posts, mostly selling" is impossible to judge until you see which
 * days it lands on and how many of your post allowance it eats.
 */
const BALANCED = MIX_PRESETS.find((p) => p.key === "balanced")!.mix;

export function PlanMonthPage() {
  const { t } = useTranslation();
  const brands = useBrands();
  const [brandId, setBrandId] = useState<string | null>(null);

  useEffect(() => {
    if (!brandId && brands.data?.length) setBrandId(brands.data[0]!.id);
  }, [brands.data, brandId]);

  const plans = useMonthlyPlans(brandId);
  const preview = usePreviewMonth();
  const create = useCreateMonthlyPlan(brandId);
  const generate = useGenerateMonthlyPlan(brandId);
  const cancel = useCancelMonthlyPlan(brandId);

  // Default to next month: planning the month you are already halfway through
  // is rarely what anyone wants.
  const [month, setMonth] = useState(() => {
    const now = new Date();
    const next = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1));
    return next.toISOString().slice(0, 7);
  });
  const [postCount, setPostCount] = useState(16);
  const [mix, setMix] = useState<ContentMix>({ ...BALANCED });
  const [plan, setPlan] = useState<MonthPreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** Empty = every connected page for this brand. */
  const [pages, setPages] = useState<string[]>([]);
  const [notice, setNotice] = useState<string | null>(null);

  const mixTotal = useMemo(
    () => MIX_BUCKETS.reduce((sum, b) => sum + mix[b], 0),
    [mix],
  );

  function setBucket(bucket: MixBucket, value: number) {
    setMix((m) => ({ ...m, [bucket]: value }));
    setPlan(null);
  }

  async function onPreview() {
    if (!brandId) return;
    setError(null);
    try {
      setPlan(
        await preview.mutateAsync({
          brandId,
          month: `${month}-01`,
          postCount,
          contentMix: mix,
          socialAccountIds: pages,
        }),
      );
    } catch (err) {
      setPlan(null);
      setError(apiErrorMessage(err, t("planMonth.previewFailed")));
    }
  }

  async function onConfirm() {
    if (!brandId) return;
    setError(null);
    try {
      const result = await create.mutateAsync({
        brandId,
        month: `${month}-01`,
        postCount,
        contentMix: mix,
        includeCampaignIds: [],
        socialAccountIds: pages,
      });
      setNotice(t("planMonth.created", { count: result.postCount }));
      setPlan(null);
    } catch (err) {
      setError(apiErrorMessage(err, t("planMonth.createFailed")));
    }
  }

  async function onGenerate(planId: string) {
    setError(null);
    setNotice(null);
    try {
      const result = await generate.mutateAsync(planId);
      setNotice(
        result.failed > 0
          ? t("planMonth.generatedPartial", {
              generated: result.generated,
              failed: result.failed,
            })
          : t("planMonth.generated", {
              count: result.generated,
              sent: result.approvalSent?.sent ?? 0,
            }),
      );
    } catch (err) {
      setError(apiErrorMessage(err, t("planMonth.generateFailed")));
    }
  }

  if (brands.isLoading) return <p className="text-sm text-muted">{t("errors.loading")}</p>;
  if (!brands.data?.length) return <p className="text-sm text-muted">{t("planMonth.noBrands")}</p>;

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2.5 text-xl font-semibold tracking-tight text-ink">
            <CalendarRange size={18} className="text-primary-600" />
            {t("planMonth.title")}
          </h1>
          <p className="text-sm text-muted">{t("planMonth.subtitle")}</p>
        </div>
        <select
          value={brandId ?? ""}
          onChange={(e) => {
            setBrandId(e.target.value);
            setPlan(null);
          }}
          className="select"
        >
          {brands.data.map((b) => (
            <option key={b.id} value={b.id}>
              {b.name}
            </option>
          ))}
        </select>
      </header>

      {error ? <ErrorBox>{error}</ErrorBox> : null}
      {notice ? (
        <div className="rounded-lg border border-green-300 bg-green-50 px-3 py-2 text-sm text-green-800">
          {notice}
        </div>
      ) : null}

      {plans.data?.length ? (
        <section className="card-pad">
          <h2 className="mb-3 text-sm font-semibold">{t("planMonth.yours")}</h2>
          <ul className="space-y-2">
            {plans.data.map((p) => (
              <li
                key={p.id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border px-3 py-2"
              >
                <div>
                  <span className="font-medium">{p.month.slice(0, 7)}</span>
                  <span className="ml-2 text-xs text-muted">
                    {t("planMonth.postCount", { count: p._count.posts })}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <BatchProgress posts={p.posts ?? []} storedStatus={p.status} />
                  {p.status === "draft" ? (
                    <button
                      onClick={() => void onGenerate(p.id)}
                      disabled={generate.isPending}
                      className="btn-primary btn-sm"
                    >
                      <Play size={13} />
                      {generate.isPending ? t("planMonth.generating") : t("planMonth.generate")}
                    </button>
                  ) : null}
                  <button
                    onClick={() => void cancel.mutateAsync(p.id)}
                    className="text-muted hover:text-red-600"
                    aria-label={t("planMonth.cancel")}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {/* Step 1 */}
      <Step number={1} title={t("planMonth.step1")}>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block text-sm">
            <span className="mb-1 block font-medium">{t("planMonth.monthLabel")}</span>
            <input
              type="month"
              value={month}
              onChange={(e) => {
                setMonth(e.target.value);
                setPlan(null);
              }}
              className="input"
            />
          </label>
          <label className="block text-sm">
            <span className="mb-1 block font-medium">
              {t("planMonth.postCountLabel", { count: postCount })}
            </span>
            <input
              type="range"
              min={1}
              max={Math.min(60, daysInMonth(Number(month.slice(0, 4)), Number(month.slice(5, 7))) * 2)}
              value={postCount}
              onChange={(e) => {
                setPostCount(Number(e.target.value));
                setPlan(null);
              }}
              className="w-full"
            />
          </label>

          <PagePicker
            brandId={brandId}
            value={pages}
            onChange={(ids) => {
              setPages(ids);
              // The preview counts posts per page, so it is no longer valid.
              setPlan(null);
            }}
          />
        </div>
      </Step>

      {/* Step 2 */}
      <Step number={2} title={t("planMonth.step2")}>
        <div className="mb-3 grid gap-2 sm:grid-cols-3">
          {MIX_PRESETS.map((preset) => (
            <button
              key={preset.key}
              onClick={() => {
                setMix({ ...preset.mix });
                setPlan(null);
              }}
              className="rounded-lg border border-border p-3 text-left text-sm hover:border-primary-400 hover:bg-primary-50"
            >
              <span className="font-medium">{t(preset.labelKey)}</span>
              <span className="mt-0.5 block text-xs text-muted">{t(preset.descriptionKey)}</span>
            </button>
          ))}
        </div>

        <div className="space-y-2">
          {MIX_BUCKETS.map((bucket) => (
            <label key={bucket} className="block text-sm">
              <span className="mb-1 flex justify-between">
                <span className="font-medium">{t(`planMonth.buckets.${bucket}`)}</span>
                <span className="text-muted">{mix[bucket]}%</span>
              </span>
              <input
                type="range"
                min={0}
                max={100}
                value={mix[bucket]}
                onChange={(e) => setBucket(bucket, Number(e.target.value))}
                className="w-full"
              />
            </label>
          ))}
        </div>

        {/* The sliders are allowed to drift; the server normalises. Saying so
            is kinder than silently rescaling behind their back. */}
        <p className="mt-2 text-xs text-muted">
          {mixTotal === 100
            ? t("planMonth.mixExact")
            : t("planMonth.mixNormalised", { total: mixTotal })}
        </p>
      </Step>

      {/* Step 3 */}
      <Step number={3} title={t("planMonth.step3")}>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => void onPreview()}
            disabled={preview.isPending}
            className="rounded-lg border border-border px-3 py-2 text-sm hover:bg-gray-50 disabled:opacity-50"
          >
            {preview.isPending ? t("planMonth.previewing") : t("planMonth.preview")}
          </button>
          <button
            onClick={() => void onConfirm()}
            disabled={create.isPending || !plan || plan.posts.length === 0}
            className="btn-primary btn-sm"
            title={!plan ? t("planMonth.previewFirst") : undefined}
          >
            <Check size={15} /> {t("planMonth.confirm")}
          </button>
        </div>

        {plan ? <MonthCalendar plan={plan} month={month} /> : null}
      </Step>
    </div>
  );
}

function Step({
  number,
  title,
  children,
}: {
  number: number;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="card-pad">
      <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold">
        <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary-600 text-[11px] font-bold text-white">
          {number}
        </span>
        {title}
      </h2>
      {children}
    </section>
  );
}

/** The calendar preview the spec asks for before confirming. */
function MonthCalendar({ plan, month }: { plan: MonthPreview; month: string }) {
  const { t } = useTranslation();

  const year = Number(month.slice(0, 4));
  const monthNumber = Number(month.slice(5, 7));
  const total = daysInMonth(year, monthNumber);

  // Monday-first grid, which is the common convention outside the US and
  // matches the schedule screen's day order starting at Mon in practice.
  const firstWeekday = new Date(Date.UTC(year, monthNumber - 1, 1)).getUTCDay();
  const leading = (firstWeekday + 6) % 7;

  const byDay = new Map<string, string[]>();
  for (const post of plan.posts) {
    const list = byDay.get(post.day) ?? [];
    list.push(post.bucket);
    byDay.set(post.day, list);
  }
  const campaignDays = new Set(plan.campaignDays);

  return (
    <div className="mt-3">
      <p className="mb-2 text-sm font-medium">
        {t("planMonth.willUse", {
          count: plan.postsConsumed,
          pages: plan.socialAccountIds.length,
        })}
      </p>

      {plan.shortfall ? (
        <p className="mb-2 flex items-start gap-1.5 rounded border border-amber-300 bg-amber-50 px-2 py-1.5 text-xs text-amber-900">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          {plan.shortfall.reason}
        </p>
      ) : null}

      <div className="overflow-x-auto">
        <div className="grid min-w-[22rem] grid-cols-7 gap-1 text-center text-[11px]">
          {["mon", "tue", "wed", "thu", "fri", "sat", "sun"].map((d) => (
            <div key={d} className="py-1 font-semibold text-muted">
              {t(`schedule.days.${d}`)}
            </div>
          ))}

          {Array.from({ length: leading }, (_, i) => (
            <div key={`pad-${i}`} />
          ))}

          {Array.from({ length: total }, (_, i) => {
            const day = `${month}-${String(i + 1).padStart(2, "0")}`;
            const buckets = byDay.get(day) ?? [];
            const isCampaign = campaignDays.has(day);

            return (
              <div
                key={day}
                className={`min-h-[3rem] rounded border p-1 ${
                  isCampaign
                    ? "border-purple-300 bg-purple-50"
                    : buckets.length
                      ? "border-primary-300 bg-primary-50"
                      : "border-border"
                }`}
                title={
                  isCampaign
                    ? t("planMonth.campaignDay")
                    : buckets.map((b) => t(`planMonth.buckets.${b}`)).join(", ")
                }
              >
                <div className="text-muted">{i + 1}</div>
                {buckets.map((bucket, n) => (
                  <div key={n} className="truncate text-[10px] font-medium text-primary-800">
                    {t(`planMonth.bucketsShort.${bucket}`)}
                  </div>
                ))}
                {isCampaign ? (
                  <div className="text-[10px] font-medium text-purple-800">
                    {t("planMonth.campaignShort")}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>

      <p className="mt-2 text-xs text-muted">{t("planMonth.calendarNote")}</p>
    </div>
  );
}
